// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { Currency } from '@/currency/api/types';
import { serializeOptionalCurrencyForAPI } from '@/currency/utilities/serialize';
import { TransactionType, projectSchema } from '@/project/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

const createProjectResponseSchema = z
  .object({
    data: projectSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, project: data }));

type CreateProjectResponse = z.infer<typeof createProjectResponseSchema>;

interface CreateProjectLineItem {
  type: 'PRODUCT' | 'PART';
  itemId: string;
  transactionType: TransactionType;
  count: number;
  locationId?: string;
}

export interface CreateProjectParameters {
  name: string;
  start?: DateTime;
  end?: DateTime;
  totalContractValue?: Currency | null;
  defaultLocationId?: string;
  customerId?: string;
  previousProjectId?: string;
  lineItems: CreateProjectLineItem[];
  inheritPreviousLineItems: boolean;
  fileIds?: string[];
  workflowId?: string | null;
  workflowStageId?: string | null;
}

/**
 * Creates a new project.
 *
 * @param parameters.name - The name of the project.
 * @param parameters.start - The start date of the project.
 * @param parameters.end - The end date of the project.
 * @param parameters.defaultLocationId - The ID of the default location for the project.
 * @param parameters.customerId - The ID of the customer associated with the
 * project.
 * @param parameters.previousProjectId - The ID of the previous project.
 * @param parameters.lineItems - A collection of line items (part or product)
 * representing an item ID, count, and optional location.
 * @param parameters.inheritPreviousLineItems - If this is set to true, this
 * renewal should use the previous line items.
 * @param parameters.fileKeys - The keys of the files to associate with the project.
 * @param parameters.workflowId - The ID of the workflow to assign to the project (nullable).
 * @param parameters.workflowStageId - The ID of the workflow stage to assign to the project (nullable).
 */
export async function createProject(
  parameters: CreateProjectParameters
): Promise<CreateProjectResponse> {
  const url = '/v1/project';
  const response = await api.post(url, {
    name: parameters.name,
    startOn: serializeDateForAPI(parameters.start),
    endOn: serializeDateForAPI(parameters.end),
    totalContractValue: serializeOptionalCurrencyForAPI(
      parameters.totalContractValue
    ),
    defaultLocationId: parameters.defaultLocationId,
    customerId: parameters.customerId,
    previousProjectId: parameters.previousProjectId,
    inheritPreviousLineItems: parameters.inheritPreviousLineItems,
    lineItems: parameters.lineItems.map((lineItem) => ({
      itemId: lineItem.itemId,
      transactionType: lineItem.transactionType,
      quantity: lineItem.count,
      locationId: lineItem.locationId,
    })),
    fileIds: parameters.fileIds,
    workflowId: parameters.workflowId,
    workflowStageId: parameters.workflowStageId,
  });

  return parseResponse(createProjectResponseSchema, response.data, {
    schemaName: 'CreateProject',
    endpoint: url,
  });
}
